import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Page, Pagebar, PagebarActions, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { IMFPStore } from '../../../../modules/stores';
import {
	Entities,
	IPerformanceOptionsFilterType,
	PerformancesColumns,
	useReportsList,
} from '../../../../modules/hooks/apis/use-reports-list';
import { useMetricsGet } from '../../../../modules/hooks/tools/use-metrics-get';
import { availablePerformanceToMetrics } from '../../../../modules/models/performance.model';
import { useMetrics } from '../../../../modules/hooks/tools/use-metrics';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useChannelHeaders } from '../../../../modules/hooks/theaders/channel.headers';
import { useChannelActions } from '../../../../modules/hooks/actions/channel.actions';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { EntityActionsMenu } from '../../../../modules/components/entity-actions-menu/entity-actions-menu';
import { useAccountActions } from '../../../../modules/hooks/actions/account.actions';
import { ManifestoChart } from '../../../../modules/components/chart/manifesto-chart';
import styles from './../../../../styles/pages.module.scss';
import { Helmet } from 'react-helmet-async';
import { PaginationState } from '@tanstack/react-table';
import { isFetchFromBackend } from '../../../../modules/helpers/reports.helpers';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';

export const AccountChannels = observer(() => {
	const { channelStore, accountStore } = useStores<IMFPStore>();
	const { accountId } = useParams();
	const { currentRouteWithoutSpecialChars } = useRoute();

	const reportsStore = channelStore.getReportsStore();
	const reportData = reportsStore.getData();
	const metricsStore = channelStore.getMetricsStore();
	const currentSegments = channelStore.getSegments();
	const accountName = accountStore.getCrud().getData().getName();

	if (!accountId) {
		return null;
	}

	const { reportsError, fetchReports } = useReportsList(
		reportsStore,
		{
			entity: Entities.Channels,
			filters: [
				{
					column: PerformancesColumns.AccountId,
					filterType: IPerformanceOptionsFilterType.Equals,
					value: [accountId],
				},
			],
			groupBys: [PerformancesColumns.Channel],
		},
		undefined,
		currentSegments
	);

	const { metricsError, fetchMetrics } = useMetricsGet(metricsStore, {
		entity: Entities.Chart,
		groupBys: [PerformancesColumns.Date],
		filters: [
			{
				column: PerformancesColumns.AccountId,
				filterType: IPerformanceOptionsFilterType.Include,
				value: [accountId],
			},
		],
	});

	const { columns } = useChannelHeaders({ summary: reportData?.getSummary() });
	const { FiltersActions, SegmentActions } = useChannelActions({ currentSegments });
	const { metrics, xLabels } = useMetrics(metricsStore.getData());
	const { EntityActions } = useAccountActions();
	const account = accountStore.getCrud().getData(); // This account will be stored from breadcrumbs

	return (
		<Page unstyled={true} className={styles.article}>
			<Helmet>
				<title>{accountName ? accountName : 'Account'} - Channels</title>
			</Helmet>
			<Pagebar classes={{ pagebar: styles.padding }}>
				<PagebarTitle>Channels</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
					<EntityActionsMenu title={'Account Actions'}>
						<EntityActions entity={account} />
					</EntityActionsMenu>
				</PagebarActions>
			</Pagebar>
			<ManifestoChart
				isLoading={metricsStore.getIsLoading()}
				metrics={metrics}
				labels={xLabels}
				legendOptions={Object.keys(availablePerformanceToMetrics)}
				isError={!!metricsError && !isAbortError(metricsError)}
			/>
			<SpacerY y={2} />
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				columns={columns}
				classes={{ wrapper: styles.padding }}
				data={reportData?.getData() || []}
				isLoading={reportsStore.getIsLoading()}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'channels_account_table')] }}
				isWithPerformance={true}
				isLoadingPerformance={reportsStore.getIsLoading()}
				defaultSortBy={[{ id: 'revenue', desc: true }]}
				fetchReports={fetchReports}
				fetchMetrics={fetchMetrics}
				onPageChange={(state: PaginationState) => {
					if (isFetchFromBackend(reportData, state) && !reportsStore.getIsLoading()) {
						fetchReports({ appendData: true, resetOffset: false });
					}
				}}
				isError={!!reportsError && !isAbortError(reportsError)}>
				{() => {
					return {
						FiltersActions,
						SegmentActions,
					};
				}}
			</ControlledTable>
		</Page>
	);
});
